import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, InputAdornment, TextField, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

import {
  MAX_TOPUP,
  MIN_TOPUP,
  RECHARGE_STATUS,
  createRechargeOrder,
  getRechargeStatus,
  isTerminal,
  verifyRecharge,
} from 'Api/walletRechargeAPI';
import useAuthStore from '../../stores/useAuthStore';
import useRazorpayCheckout from '../../hooks/useRazorpayCheckout';
import { typography, size } from 'theme/tokens';

/**
 * Add money to the partner's INR wallet with UPI or a card.
 *
 * ── ⭐ THE ONE RULE ─────────────────────────────────────────────────────────
 * Success is announced on `status: 'SUCCESS'` from the engine, and on nothing
 * else. A resolved `verifyRecharge` only means the Checkout signature checked
 * out; the wallet is credited when Razorpay's webhook reaches the booking
 * engine, which happens out of band and may land before or after the browser
 * comes back. Showing "added" off the verify response would tell partners their
 * money arrived before it had.
 *
 * ── Which makes polling the spine, not a fallback ──────────────────────────
 * A partner paying by UPI approves in their banking app and frequently never
 * returns to this tab. Checkout's handler never fires, and the webhook credits
 * them anyway. So every exit from the sheet — paid, dismissed, or failed —
 * leads to the same place: poll `getRechargeStatus` until it settles.
 *
 * ── A poll timeout is not a failure ────────────────────────────────────────
 * If we stop polling before the engine settles, the money is still coming: the
 * webhook, or the engine's reconciliation sweep behind it, will credit it. The
 * copy in that state says so rather than implying the payment was lost, because
 * a partner told "failed" for a payment that later lands will raise a ticket and
 * may well pay twice.
 */

/** Every 2.5s for ~2 minutes. The credit normally lands in a few seconds. */
const POLL_INTERVAL_MS = 2500;
const POLL_TIMEOUT_MS = 120000;

const PHASE = {
  FORM: 'form',
  CREATING: 'creating',
  AWAITING_PAYMENT: 'awaiting_payment',
  CONFIRMING: 'confirming',
  DONE: 'done',
  SLOW: 'slow',
  FAILED: 'failed',
};

const rupees = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

/** The MUI stand-in for antd's <Result>: an icon, a headline, a line of copy. */
function Outcome({ icon, title, subtitle, color }) {
  return (
    <Box sx={{ textAlign: 'center', py: 3, display: 'grid', gap: 1, justifyItems: 'center' }}>
      <Box sx={{ color, display: 'flex' }}>{icon}</Box>
      <Typography variant="h6" sx={{ fontWeight: typography.fontWeight.semiBold }}>{title}</Typography>
      {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
    </Box>
  );
}

export default function TopUpModal({ open, onClose, onCredited }) {
  const partner = useAuthStore((s) => s.partner);
  const user = useAuthStore((s) => s.user);
  // Session's chosen branch — actingBranchCode when the branch switcher fired,
  // else the user's pinned branch, else `0`. Same resolution as the Revenue
  // writes on this portal — see partner MarkupRulesPage.
  const actingBranchCode = useAuthStore((s) => s.actingBranchCode);
  const userBranchCode = useAuthStore((s) => s.user?.branchCode);
  const branchCodeForWire = Number(actingBranchCode ?? userBranchCode) || 0;
  const { open: openCheckout, close: closeCheckout } = useRazorpayCheckout();

  const [amount, setAmount] = useState('');
  const [phase, setPhase] = useState(PHASE.FORM);
  const [settled, setSettled] = useState(null);
  const [error, setError] = useState(null);

  // Survives re-renders and, more importantly, lets the unmount cleanup stop a
  // poll that would otherwise keep calling after the modal is gone.
  const pollTimer = useRef(null);
  const alive = useRef(true);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) clearTimeout(pollTimer.current);
    pollTimer.current = null;
  }, []);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      stopPolling();
      closeCheckout();
    };
  }, [stopPolling, closeCheckout]);

  const reset = useCallback(() => {
    stopPolling();
    setAmount('');
    setPhase(PHASE.FORM);
    setSettled(null);
    setError(null);
  }, [stopPolling]);

  /**
   * Poll until the engine settles the attempt or we run out of patience.
   *
   * Read errors are swallowed on purpose: a single failed status call during a
   * top-up that is otherwise fine should not become a visible failure, because
   * the credit does not depend on this browser at all. The timeout is the only
   * thing that ends an unsettled poll.
   */
  const pollUntilSettled = useCallback(
    (rechargeId) => {
      const deadline = Date.now() + POLL_TIMEOUT_MS;
      setPhase(PHASE.CONFIRMING);

      const tick = async () => {
        if (!alive.current) return;

        const result = await getRechargeStatus(rechargeId);
        if (!alive.current) return;

        const recharge = result.ok ? result.data : null;

        if (recharge && isTerminal(recharge.status)) {
          setSettled(recharge);
          if (recharge.status === RECHARGE_STATUS.SUCCESS) {
            setPhase(PHASE.DONE);
            onCredited?.(recharge);
          } else {
            setPhase(PHASE.FAILED);
          }
          return;
        }

        if (Date.now() >= deadline) {
          setSettled(recharge);
          setPhase(PHASE.SLOW);
          return;
        }

        pollTimer.current = setTimeout(tick, POLL_INTERVAL_MS);
      };

      tick();
    },
    [onCredited],
  );

  const start = useCallback(async () => {
    setError(null);
    setPhase(PHASE.CREATING);

    const created = await createRechargeOrder(Number(amount), branchCodeForWire);
    if (!alive.current) return;

    if (!created.ok) {
      setError(created.message);
      setPhase(PHASE.FORM);
      return;
    }

    const order = created.data;
    setPhase(PHASE.AWAITING_PAYMENT);

    try {
      await openCheckout({
        order,
        // Empty keys OMITTED rather than sent blank: Checkout treats '' as an
        // asserted-but-empty field and renders it as an error instead of simply
        // asking. `phone` is not in the session whitelist today, so contact is
        // normally absent and Razorpay collects it itself.
        prefill: Object.fromEntries(
          Object.entries({
            name: user?.name || partner?.partnerName,
            email: user?.email,
            contact: user?.phone,
          }).filter(([, v]) => v),
        ),
        // The browser came back with a signature. Hand it over so the attempt
        // is marked verified, then poll — the credit is the webhook's job.
        onHandoff: async (response) => {
          await verifyRecharge({
            rechargeId: order.rechargeId,
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
          });
          if (alive.current) pollUntilSettled(order.rechargeId);
        },
        // Closed the sheet. A UPI payment may still be in flight, so this is
        // not a cancellation and we keep watching.
        onDismiss: () => {
          if (alive.current) pollUntilSettled(order.rechargeId);
        },
        // Razorpay reported the payment failed. The engine hears the same thing
        // on its webhook, so let the poll read the authoritative result rather
        // than declaring failure from the browser's copy of it.
        onFailed: () => {
          if (alive.current) pollUntilSettled(order.rechargeId);
        },
      });
    } catch (loadError) {
      if (!alive.current) return;
      setError(loadError.message || 'Could not open the payment window.');
      setPhase(PHASE.FORM);
    }
  }, [amount, branchCodeForWire, openCheckout, partner, pollUntilSettled, user]);

  const finish = useCallback(() => {
    reset();
    onClose?.();
  }, [onClose, reset]);

  const numeric = Number(amount);
  const amountValid = /^\d+$/.test(String(amount)) && numeric >= MIN_TOPUP && numeric <= MAX_TOPUP;
  const busy = phase === PHASE.CREATING || phase === PHASE.AWAITING_PAYMENT || phase === PHASE.CONFIRMING;

  return (
    <Dialog open={open} onClose={busy ? undefined : finish} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: typography.fontWeight.semiBold }}>Add money</DialogTitle>

      <DialogContent dividers>
        {phase === PHASE.FORM && (
          <Box sx={{ display: 'grid', gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField
              autoFocus
              fullWidth
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter' && amountValid) start(); }}
              placeholder="Whole rupees"
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              helperText={`${rupees(MIN_TOPUP)} to ${rupees(MAX_TOPUP)}, in whole rupees. Paid by UPI or card, in INR.`}
              inputProps={{ inputMode: 'numeric', 'aria-label': 'Amount in rupees' }}
            />
          </Box>
        )}

        {phase === PHASE.CREATING && (
          <Outcome icon={<CircularProgress size={size[36]} />} title="Setting up your payment…" />
        )}

        {phase === PHASE.AWAITING_PAYMENT && (
          <Outcome
            icon={<CircularProgress size={size[36]} />}
            title="Complete the payment"
            subtitle="Finish in the Razorpay window. If you close it after paying, we will still pick the payment up."
          />
        )}

        {phase === PHASE.CONFIRMING && (
          <Outcome
            icon={<CircularProgress size={size[36]} />}
            title="Confirming your payment"
            subtitle="This usually takes a few seconds. Please keep this window open."
          />
        )}

        {phase === PHASE.DONE && (
          <Outcome
            icon={<CheckCircleOutlineIcon sx={{ fontSize: typography.fontSize[48] }} />}
            color="success.main"
            title={`${rupees(settled?.amount)} added`}
            subtitle={
              settled?.txnReference
                ? `Paid by ${String(settled.paymentMethod || '').toLowerCase() || 'card'} · Reference ${settled.txnReference}`
                : 'Your wallet balance has been updated.'
            }
          />
        )}

        {phase === PHASE.FAILED && (
          <Outcome
            icon={<ErrorOutlineIcon sx={{ fontSize: typography.fontSize[48] }} />}
            color="error.main"
            title={settled?.status === RECHARGE_STATUS.EXPIRED ? 'This top-up expired' : 'Payment not completed'}
            subtitle={settled?.failureReason || 'No money was taken. You can try again, or use a different payment method.'}
          />
        )}

        {phase === PHASE.SLOW && (
          <Outcome
            icon={<AccessTimeIcon sx={{ fontSize: typography.fontSize[48] }} />}
            color="warning.main"
            title="Still confirming"
            subtitle="Your payment is going through — it is taking longer than usual to confirm. Your balance will update on its own; no need to pay again."
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {phase === PHASE.FORM ? (
          <>
            <Button onClick={finish} color="inherit">Cancel</Button>
            <Button variant="contained" disabled={!amountValid} onClick={start}>
              Continue to payment
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={finish} disabled={busy}>Done</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
